
import React, { useState, useEffect, useMemo } from 'react';
import { dbService } from '../services/dbService.ts';
import { SubscriptionTier, User, ProfessionalProfile, ChatRequest, UserRole } from '../types.ts';

const AdminView: React.FC<{ onLogout: () => void }> = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'features' | 'finance' | 'infra' | 'branding'>('overview');
  const [users, setUsers] = useState<User[]>(dbService.getUsers());
  const [profProfiles, setProfProfiles] = useState<ProfessionalProfile[]>(dbService.getAllProfessionalProfiles());
  const [requests, setRequests] = useState<ChatRequest[]>(dbService.getRequests());
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  
  // User Management State
  const [userSearch, setUserSearch] = useState("");
  const [userFilter, setUserFilter] = useState<'all' | 'student' | 'professional'>('all');

  // Branding state
  const [logoUrl, setLogoUrl] = useState(dbService.getSettings().logoUrl || '');
  const [isSavingBranding, setIsSavingBranding] = useState(false);

  // Feature Flags
  const [features, setFeatures] = useState([
    { id: 'ai_engine', name: 'Gemini Synergy Engine', status: 'operational', version: 'v3.1.0', environment: 'production' },
    { id: 'veo_video', name: 'Veo Video Generation', status: 'beta', version: 'v1.0.0-rc', environment: 'staging' },
    { id: 'realtime_chat', name: 'WebSocket Chat Layer', status: 'operational', version: 'v2.4.1', environment: 'production' },
    { id: 'stripe_payments', name: 'Stripe Billing Gateway', status: 'operational', version: 'v4.2.0', environment: 'production' },
    { id: 'analytics', name: 'Telemetry Pipeline', status: 'degraded', version: 'v1.1.2', environment: 'production' },
    { id: 'email_service', name: 'SMTP Relay Service', status: 'operational', version: 'v2.0.0', environment: 'production' }
  ]);

  const TIER_PRICES = {
    [SubscriptionTier.BASIC]: 0,
    [SubscriptionTier.STANDARD]: 14.99,
    [SubscriptionTier.ADVANCED]: 24.99
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setUsers(dbService.getUsers());
      setRequests(dbService.getRequests());
      setProfProfiles(dbService.getAllProfessionalProfiles());
      setLastRefreshed(new Date());
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const revenueStats = useMemo(() => {
    const standardCount = users.filter(u => u.tier === SubscriptionTier.STANDARD).length;
    const advancedCount = users.filter(u => u.tier === SubscriptionTier.ADVANCED).length;
    const standardRev = standardCount * TIER_PRICES[SubscriptionTier.STANDARD];
    const advancedRev = advancedCount * TIER_PRICES[SubscriptionTier.ADVANCED];
    const totalMRR = standardRev + advancedRev;
    return { standardCount, advancedCount, standardRev, advancedRev, totalMRR };
  }, [users]);

  const costStats = useMemo(() => {
    const computeCost = 145.50;
    const dbCost = 42.00;
    const aiUsageCost = requests.length * 0.052;
    const totalOpEx = computeCost + dbCost + aiUsageCost;
    return { computeCost, dbCost, aiUsageCost, totalOpEx };
  }, [requests]);

  const offlineProfs = useMemo(() => {
    return profProfiles.filter(p => p.maxMeetingsPerWeek === 0);
  }, [profProfiles]);

  const handleSaveBranding = async () => {
    setIsSavingBranding(true);
    dbService.saveSettings({ logoUrl });
    window.dispatchEvent(new Event('cc_settings_updated'));
    await new Promise(r => setTimeout(r, 1000));
    setIsSavingBranding(false);
    alert("Branding Protocol Synced Successfully.");
  };

  const toggleFeatureStatus = (id: string) => {
    setFeatures(features.map(f => {
      if (f.id === id) {
        return { ...f, status: f.status === 'operational' ? 'maintenance' : 'operational' };
      }
      return f;
    }));
  };

  const deleteUser = (id: string) => {
    if (confirm("CRITICAL: Are you sure you want to scrub this identity?")) {
      dbService.deleteUser(id);
      setUsers(dbService.getUsers());
    }
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'operational': return 'bg-emerald-500';
      case 'beta': return 'bg-blue-500';
      case 'degraded': return 'bg-amber-500';
      case 'maintenance': return 'bg-red-500';
      default: return 'bg-slate-300';
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.fullName.toLowerCase().includes(userSearch.toLowerCase()) || u.email.toLowerCase().includes(userSearch.toLowerCase());
    const matchesRole = userFilter === 'all' || u.role === userFilter;
    return matchesSearch && matchesRole;
  });

  const renderOverview = () => (
    <div className="space-y-10 animate-in fade-in duration-500">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-xl">
          <p className="text-slate-400 text-[9px] font-black uppercase tracking-widest mb-4">Net MRR</p>
          <p className={`text-3xl font-black tracking-tighter ${(revenueStats.totalMRR - costStats.totalOpEx) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            $ {(revenueStats.totalMRR - costStats.totalOpEx).toFixed(0)}
          </p>
          <div className="w-full bg-slate-100 h-1.5 rounded-full mt-4 overflow-hidden">
            <div className="bg-emerald-500 h-full w-[78%]"></div>
          </div>
        </div>
        <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-xl">
          <p className="text-slate-400 text-[9px] font-black uppercase tracking-widest mb-4">Active Nodes</p>
          <p className="text-3xl font-black tracking-tighter text-blue-900">{users.length}</p>
          <p className="text-[9px] font-bold text-slate-400 mt-2">+12% vs last week</p>
        </div>
        <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-xl">
          <p className="text-slate-400 text-[9px] font-black uppercase tracking-widest mb-4">Synergy Events</p>
          <p className="text-3xl font-black tracking-tighter text-indigo-600">{requests.length}</p>
          <p className="text-[9px] font-bold text-slate-400 mt-2">Total Connections</p>
        </div>
        <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-xl relative overflow-hidden">
          <p className="text-slate-400 text-[9px] font-black uppercase tracking-widest mb-4">System Alerts</p>
          <p className="text-3xl font-black tracking-tighter text-amber-500">{offlineProfs.length + 1}</p>
          <p className="text-[9px] font-bold text-slate-400 mt-2">Requires Attention</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white p-10 rounded-[3rem] border border-slate-100 shadow-2xl">
           <div className="flex justify-between items-center mb-8">
             <h3 className="text-lg font-black text-blue-950 flex items-center gap-3">
               <i className="fas fa-globe-americas text-blue-500"></i> Regional Traffic
             </h3>
             <button className="text-[9px] font-black text-blue-600 uppercase tracking-widest border border-blue-100 px-3 py-1 rounded-lg">Real-time</button>
           </div>
           <div className="space-y-6">
              {[
                { r: 'North America (US-EAST)', v: 4500, c: 'bg-blue-600' },
                { r: 'Europe (EU-WEST)', v: 3200, c: 'bg-indigo-500' },
                { r: 'Asia Pacific (AP-SOUTH)', v: 1800, c: 'bg-violet-500' },
              ].map((region, i) => (
                <div key={i} className="space-y-2">
                   <div className="flex justify-between text-xs font-bold text-slate-600">
                      <span>{region.r}</span>
                      <span>{region.v.toLocaleString()} req/s</span>
                   </div>
                   <div className="w-full bg-slate-50 h-2 rounded-full overflow-hidden">
                      <div className={`${region.c} h-full rounded-full`} style={{ width: `${(region.v / 5000) * 100}%` }}></div>
                   </div>
                </div>
              ))}
           </div>
        </div>

        <div className="bg-blue-950 p-10 rounded-[3rem] text-white shadow-2xl relative overflow-hidden">
           <h3 className="text-lg font-black mb-8 flex items-center gap-3">
             <i className="fas fa-microchip text-blue-400"></i> Compute Health
           </h3>
           <div className="grid grid-cols-2 gap-6 relative z-10">
              <div className="p-6 bg-white/5 rounded-2xl border border-white/10 backdrop-blur-sm">
                 <p className="text-[9px] font-black uppercase tracking-widest text-blue-300 mb-2">CPU Load</p>
                 <p className="text-3xl font-black">24%</p>
                 <p className="text-[9px] text-emerald-400 mt-2"><i className="fas fa-check-circle"></i> Optimal</p>
              </div>
              <div className="p-6 bg-white/5 rounded-2xl border border-white/10 backdrop-blur-sm">
                 <p className="text-[9px] font-black uppercase tracking-widest text-blue-300 mb-2">Memory</p>
                 <p className="text-3xl font-black">4.2GB</p>
                 <p className="text-[9px] text-blue-300 mt-2">of 16GB Alloc</p>
              </div>
              <div className="p-6 bg-white/5 rounded-2xl border border-white/10 backdrop-blur-sm">
                 <p className="text-[9px] font-black uppercase tracking-widest text-blue-300 mb-2">Error Rate</p>
                 <p className="text-3xl font-black">0.01%</p>
                 <p className="text-[9px] text-emerald-400 mt-2">Nominal</p>
              </div>
              <div className="p-6 bg-white/5 rounded-2xl border border-white/10 backdrop-blur-sm">
                 <p className="text-[9px] font-black uppercase tracking-widest text-blue-300 mb-2">API Latency</p>
                 <p className="text-3xl font-black">42ms</p>
                 <p className="text-[9px] text-emerald-400 mt-2">p99</p>
              </div>
           </div>
        </div>
      </div>
    </div>
  );

  const renderUsers = () => (
    <div className="space-y-8 animate-in slide-in-from-right-10 duration-500">
      <div className="bg-white p-8 rounded-[3rem] border border-slate-100 shadow-xl flex flex-col md:flex-row gap-6 justify-between items-center">
         <div className="relative w-full md:w-96">
            <i className="fas fa-search absolute left-6 top-1/2 -translate-y-1/2 text-slate-400"></i>
            <input 
              type="text" 
              placeholder="Search by name or email identity..." 
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              className="w-full pl-14 pr-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:border-blue-500 font-bold text-sm transition-all"
            />
         </div>
         <div className="flex gap-2 p-1 bg-slate-50 rounded-2xl border border-slate-200">
            {['all', 'student', 'professional'].map((role) => (
              <button 
                key={role}
                onClick={() => setUserFilter(role as any)}
                className={`px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${userFilter === role ? 'bg-blue-950 text-white shadow-md' : 'text-slate-400 hover:text-blue-900'}`}
              >
                {role}s
              </button>
            ))}
         </div>
      </div>

      <div className="bg-white rounded-[3rem] border border-slate-100 overflow-hidden shadow-2xl">
         <div className="overflow-x-auto">
           <table className="w-full text-left min-w-[800px]">
             <thead className="bg-slate-50 text-[10px] font-black uppercase text-slate-400 border-b border-slate-100">
               <tr>
                 <th className="px-10 py-6">Identity Profile</th>
                 <th className="px-10 py-6">Role & Status</th>
                 <th className="px-10 py-6">Plan Tier</th>
                 <th className="px-10 py-6">Joined</th>
                 <th className="px-10 py-6 text-right">Actions</th>
               </tr>
             </thead>
             <tbody className="divide-y divide-slate-50">
               {filteredUsers.map(u => (
                 <tr key={u.id} className="hover:bg-slate-50 transition group">
                   <td className="px-10 py-6">
                      <div className="flex items-center gap-4">
                         <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center font-black text-slate-400">{u.fullName[0]}</div>
                         <div>
                            <p className="font-black text-blue-950 text-sm">{u.fullName}</p>
                            <p className="text-[10px] font-bold text-slate-400">{u.email}</p>
                         </div>
                      </div>
                   </td>
                   <td className="px-10 py-6">
                      <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg border text-[9px] font-black uppercase tracking-widest ${u.role === UserRole.PROFESSIONAL ? 'bg-indigo-50 text-indigo-600 border-indigo-100' : 'bg-emerald-50 text-emerald-600 border-emerald-100'}`}>
                         <i className={`fas ${u.role === UserRole.PROFESSIONAL ? 'fa-briefcase' : 'fa-graduation-cap'}`}></i>
                         {u.role}
                      </span>
                   </td>
                   <td className="px-10 py-6">
                      <span className={`text-[9px] font-black px-3 py-1 rounded-lg border ${u.tier === SubscriptionTier.ADVANCED ? 'bg-amber-50 text-amber-600 border-amber-100' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                        {u.tier}
                      </span>
                   </td>
                   <td className="px-10 py-6 text-[10px] text-slate-400 font-bold">{new Date(u.createdAt).toLocaleDateString()}</td>
                   <td className="px-10 py-6 text-right">
                      <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                         <button onClick={() => deleteUser(u.id)} className="w-8 h-8 rounded-lg bg-white border border-slate-200 text-slate-400 hover:text-red-600 hover:border-red-200 flex items-center justify-center transition-colors">
                           <i className="fas fa-trash"></i>
                         </button>
                      </div>
                   </td>
                 </tr>
               ))}
             </tbody>
           </table>
         </div>
      </div>
    </div>
  );

  const renderFeatures = () => (
    <div className="space-y-8 animate-in slide-in-from-right-10 duration-500">
      <div className="bg-gradient-to-br from-blue-950 to-slate-900 p-12 rounded-[4rem] text-white shadow-3xl">
         <h3 className="text-3xl font-black tracking-tighter mb-2">Feature Control</h3>
         <p className="text-blue-300 font-medium max-w-lg">Manage enterprise-wide feature availability from the central nervous system.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
         {features.map(feat => (
           <div key={feat.id} className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-xl flex flex-col justify-between group hover:border-blue-200 transition-all">
              <div className="mb-8">
                 <div className="flex justify-between items-start mb-4">
                    <div className={`w-3 h-3 rounded-full ${getStatusColor(feat.status)} shadow-lg shadow-${getStatusColor(feat.status).split('-')[1]}-500/30 animate-pulse`}></div>
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest bg-slate-50 px-2 py-1 rounded-lg border border-slate-100">{feat.environment}</span>
                 </div>
                 <h4 className="text-lg font-black text-blue-950 mb-1">{feat.name}</h4>
                 <p className="text-xs font-bold text-slate-400">{feat.version}</p>
              </div>
              
              <div className="flex items-center justify-between pt-6 border-t border-slate-50">
                 <span className={`text-[10px] font-black uppercase tracking-widest ${feat.status === 'operational' ? 'text-emerald-600' : feat.status === 'maintenance' ? 'text-red-500' : 'text-amber-500'}`}>
                    {feat.status}
                 </span>
                 <button 
                   onClick={() => toggleFeatureStatus(feat.id)}
                   className={`w-12 h-6 rounded-full p-1 transition-colors ${feat.status === 'operational' ? 'bg-blue-950' : 'bg-slate-200'}`}
                 >
                    <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${feat.status === 'operational' ? 'translate-x-6' : 'translate-x-0'}`}></div>
                 </button>
              </div>
           </div>
         ))}
      </div>
    </div>
  );

  const renderFinance = () => (
    <div className="space-y-8 animate-in slide-in-from-right-10 duration-500">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
         <div className="bg-white p-12 rounded-[4rem] border border-slate-100 shadow-2xl">
           <h3 className="text-2xl font-black text-blue-950 tracking-tighter mb-8">Revenue Streams</h3>
           <div className="space-y-6">
              <div className="flex justify-between items-center p-6 bg-slate-50 rounded-3xl border border-slate-100">
                 <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-xl text-emerald-500 shadow-sm"><i className="fas fa-layer-group"></i></div>
                    <div>
                       <p className="font-black text-blue-950 text-sm">Standard Tier</p>
                       <p className="text-[10px] font-bold text-slate-400">{revenueStats.standardCount} Subscribers</p>
                    </div>
                 </div>
                 <p className="font-black text-xl text-blue-950">${revenueStats.standardRev.toFixed(0)}</p>
              </div>
              <div className="flex justify-between items-center p-6 bg-blue-50 rounded-3xl border border-blue-100">
                 <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-xl text-blue-600 shadow-sm"><i className="fas fa-crown"></i></div>
                    <div>
                       <p className="font-black text-blue-950 text-sm">Advanced Tier</p>
                       <p className="text-[10px] font-bold text-blue-400">{revenueStats.advancedCount} Subscribers</p>
                    </div>
                 </div>
                 <p className="font-black text-xl text-blue-950">${revenueStats.advancedRev.toFixed(0)}</p>
              </div>
           </div>
           <div className="mt-8 pt-8 border-t border-slate-100 flex justify-between items-end">
              <span className="text-xs font-black text-slate-400 uppercase tracking-widest">Total Monthly Recurring</span>
              <span className="text-4xl font-black text-emerald-500 tracking-tighter leading-none">${revenueStats.totalMRR.toFixed(0)}</span>
           </div>
         </div>

         <div className="bg-white p-12 rounded-[4rem] border border-slate-100 shadow-2xl">
           <h3 className="text-2xl font-black text-blue-950 tracking-tighter mb-8">Operational Burn</h3>
           <table className="w-full text-left">
             <tbody className="divide-y divide-slate-50">
               <tr>
                 <td className="py-4 font-bold text-slate-600 text-sm">GCP Compute Engine</td>
                 <td className="py-4 text-right font-black text-red-500">${costStats.computeCost.toFixed(2)}</td>
               </tr>
               <tr>
                 <td className="py-4 font-bold text-slate-600 text-sm">Cloud SQL Database</td>
                 <td className="py-4 text-right font-black text-red-500">${costStats.dbCost.toFixed(2)}</td>
               </tr>
               <tr>
                 <td className="py-4 font-bold text-slate-600 text-sm">Gemini AI Inference</td>
                 <td className="py-4 text-right font-black text-red-500">${costStats.aiUsageCost.toFixed(2)}</td>
               </tr>
             </tbody>
           </table>
           <div className="mt-auto pt-8 border-t border-slate-100">
               <div className="p-6 bg-red-50 rounded-3xl border border-red-100 flex justify-between items-center">
                  <span className="text-[10px] font-black text-red-400 uppercase tracking-widest">Total OpEx</span>
                  <span className="text-2xl font-black text-red-600">-${costStats.totalOpEx.toFixed(2)}</span>
               </div>
           </div>
         </div>
      </div>
    </div>
  );

  const renderBranding = () => (
    <div className="space-y-8 animate-in slide-in-from-right-10 duration-500">
      <div className="bg-white p-12 rounded-[4rem] border border-slate-100 shadow-2xl">
        <h3 className="text-3xl font-black text-blue-950 tracking-tighter mb-4">Identity Hub.</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
           <div className="space-y-6">
              <div className="space-y-2">
                 <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-2">Logo URL</label>
                 <input 
                   type="text" 
                   value={logoUrl} 
                   onChange={(e) => setLogoUrl(e.target.value)}
                   className="w-full p-6 rounded-3xl bg-slate-50 border-2 border-transparent focus:border-blue-900 outline-none font-bold text-blue-950 shadow-inner" 
                 />
              </div>
              <button 
                onClick={handleSaveBranding}
                disabled={isSavingBranding}
                className="w-full py-5 bg-blue-950 text-white rounded-[1.5rem] font-black text-xs uppercase hover:bg-blue-800 transition disabled:opacity-50"
              >
                Sync Identity
              </button>
           </div>
           <div className="bg-slate-50 p-10 rounded-[3rem] border border-slate-100 flex flex-col items-center justify-center text-center">
              <div className="bg-white p-8 rounded-[2rem] shadow-xl border border-white/50 mb-4">
                 <img src={logoUrl || 'https://via.placeholder.com/150'} className="w-20 h-20 object-contain mx-auto mb-4" />
                 <span className="text-xl font-black text-blue-950">Coffee<span className="text-blue-600">Connex</span></span>
              </div>
              <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Live Preview</p>
           </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-900 font-sans">
      <aside className="w-72 bg-blue-950 border-r border-slate-200 p-6 flex flex-col fixed h-full z-50">
        <div className="flex items-center gap-4 mb-12 px-2">
          <div className="bg-white text-blue-950 p-2.5 rounded-2xl shadow-xl"><i className="fas fa-shield-halved text-xl"></i></div>
          <div>
            <span className="text-xl font-black tracking-tight uppercase text-white block leading-none">Admin</span>
            <span className="text-[8px] font-black text-blue-400 uppercase tracking-widest mt-1">Console</span>
          </div>
        </div>
        <nav className="flex-1 space-y-2">
          <button onClick={() => setActiveTab('overview')} className={`w-full flex items-center gap-4 px-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all ${activeTab === 'overview' ? 'bg-white/10 text-white shadow-lg' : 'text-white/40 hover:text-white'}`}>
            <i className="fas fa-chart-line w-4"></i> Overview
          </button>
          <button onClick={() => setActiveTab('users')} className={`w-full flex items-center gap-4 px-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all ${activeTab === 'users' ? 'bg-white/10 text-white shadow-lg' : 'text-white/40 hover:text-white'}`}>
            <i className="fas fa-users w-4"></i> Users
          </button>
          <button onClick={() => setActiveTab('features')} className={`w-full flex items-center gap-4 px-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all ${activeTab === 'features' ? 'bg-white/10 text-white shadow-lg' : 'text-white/40 hover:text-white'}`}>
            <i className="fas fa-toggle-on w-4"></i> Features
          </button>
          <button onClick={() => setActiveTab('finance')} className={`w-full flex items-center gap-4 px-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all ${activeTab === 'finance' ? 'bg-white/10 text-white shadow-lg' : 'text-white/40 hover:text-white'}`}>
            <i className="fas fa-coins w-4"></i> Finance
          </button>
          <button onClick={() => setActiveTab('branding')} className={`w-full flex items-center gap-4 px-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all ${activeTab === 'branding' ? 'bg-white/10 text-white shadow-lg' : 'text-white/40 hover:text-white'}`}>
            <i className="fas fa-palette w-4"></i> Branding
          </button>
        </nav>
        <button onClick={onLogout} className="mt-auto py-4 bg-red-500/10 text-red-400 rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-red-500 hover:text-white transition">Logout</button>
      </aside>

      <main className="flex-1 ml-72 p-12 lg:p-20 overflow-y-auto min-h-screen">
        <header className="flex justify-between items-end mb-16 pb-12 border-b border-slate-200">
          <div>
             <span className="text-[10px] font-black text-blue-600 uppercase tracking-[0.4em] mb-2 block">Enterprise Command</span>
             <h1 className="text-5xl font-black text-blue-950 tracking-tighter capitalize">{activeTab}.</h1>
          </div>
          <p className="text-xs font-bold text-slate-400 bg-white px-4 py-2 rounded-xl border border-slate-100 shadow-sm">{lastRefreshed.toLocaleTimeString()}</p>
        </header>

        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'users' && renderUsers()}
        {activeTab === 'features' && renderFeatures()}
        {activeTab === 'finance' && renderFinance()}
        {activeTab === 'branding' && renderBranding()}
      </main>
    </div>
  );
};

export default AdminView;
